<script>
    import $date from '../../prototype/date';
    import $device from '../../prototype/device';
    import $locale from '../../prototype/locale';
    import $string from '../../prototype/string';
    import field from './ib-field.vue';
    import picker from './ib-field-date-pick.vue';

    const nativePicker = !$device.hasKeyboard;

    const pad = n => n.length < 2 ? '0' + n : n;

    const mmddyyyy = {
        en: ['mm','dd','yyyy'],
        de: ['MM','TT','JJJJ'],
        es: ['mm','dd','aaaa'],
        fr: ['mm','jj','aaaa'],
        it: ['mm','gg','aaaa'],
        ru: ['мм','дд','гггг'],
        ar: ['شهر','يوم','سنة'],
        he: ['חודש','יום','שנה']
    };

    const fmtInvalid = value =>  {
        if (value && value.length == 10) {
            const y = +value.slice(0,4);
            const m = +value.slice(5,7) - 1;
            const d = +value.slice(8,10);
            return m < 0 || m > 11 || d < 1 || d - 30 > (2773 & (1 << m)) || (m == 1 && d > (!(y % 4) && y % 100 || !(y % 400) ? 29 : 28));
        } else {
            return true;
        }
    };


    /**
     * A text input field that accepts input in a localized numeric format, accepts and emits values
     * in ISO 8601 format, and by default displays a custom date picker on desktop browsers. For
     * mobile browsers, `<input type="date">` is used, which uses a native date picker dependent on
     * platform.
     * @see This component derives from [ib-field](#ibfield).
     */
    export default {
        name: 'ib-field-date',
        mixins: [field],

        props: {
            /**
             * Value of field; ISO 8601 string or undefined for invalid date.
             */
            value: {
                type: [String,Number],
                default: undefined
            },
            /**
             * The minimum for the picker in ISO Format: YYYY-MM-DD
             */
            min: {
                type: String,
                default: '1900-01-01'
            },
            /**
             * The maximum for the picker in ISO Format: YYYY-MM-DD
             */
            max: {
                type: String,
                default: '2099-12-31'
            },
            /**
             * If true, on desktop, show a date picker when focused
             */
            picker: {
                type: Boolean,
                default: true
            },
            /**
             * If true, when a date picker is used and the field is empty,
             * display the year selector first when focused. This is useful
             * when selecting distant dates such as birth dates, where the
             * majority of users will need to zoom out to the year selector
             * anyway.
             */
            distant: Boolean,
            /**
             * Custom error string for date too early
             */
            errorUnderflow: String,
            /**
             * Custom error string for date too late
             */
            errorOverflow: String
        },

        data() {
            let formatter = $date.mmddyy;
            let sample = formatter($date.local('1970-12-31'));

            let mm = 0, mi = sample.indexOf(12);
            let dd = 1, di = sample.indexOf(31);
            let yy = 2, yi = sample.indexOf(1970);
            if (mi >= 0 && di >= 0 && yi >= 0) {
                // $date.mmddyy output is parseable
                mm = (mi > yi) + (mi > di);
                dd = (di > yi) + (di > mi);
                yy = (yi > mi) + (yi > di);
            } else {
                // $date.mmddyy output is not parseable - should not happen
                formatter = date => pad(''+(date.month+1)) + '/' + pad(''+date.day) + '/' + date.year;
            }

            let field = [];
            field[mm] =
            field[dd] = '(\\d{2})';
            field[yy] = '(\\d{4})';

            const sep = (sample.match(/(\u200e|\u200f)?[/.-]/)||['/'])[0];
            return {
                field,
                formatter,
                yy, mm, dd, sep,
                parser: RegExp('^' + field.join($string.escapeRegex(sep)) + '$'),
                match: RegExp('^\\s*' + field[0] + '\\W*' + field[1] + '?\\W*' + field[2] + '?(\\d*).*'),
                replace: '$1' + sep + '$2' + sep + '$3$4',
                validity: null,
                which: 0
            };
        },

        computed: {
            bind() {
                return nativePicker ? {
                    type: 'date',
                    min: this.minDate.toDateString(),
                    max: this.maxDate.toDateString()
                } : {
                    type: 'text',
                    maxlength: 8 + 2 * this.sep.length,
                    pattern: this.field.join($string.escapeRegex(this.sep)),
                    autocomplete: 'off',
                    autocorrect: 'off',
                    autocapitalize: 'off'
                };
            },

            _placeholder() {
                if (!nativePicker) {
                    const fmt = mmddyyyy[$locale.language] || mmddyyyy.en;
                    let r = [];
                    r[this.mm] = fmt[0];
                    r[this.dd] = fmt[1];
                    r[this.yy] = fmt[2];
                    return r.join(this.sep);
                }
            },

            minDate() {
                const r = /^[\-+]/.test(this.min)
                    ? $date.local().add(this.min).startOfDay()
                    : $date.local(this.min || null);
                if (!r.isValid()) {
                    console.error('ib-field-date: invalid format for min');
                }
                return r;
            },

            minDateStr() {
                return this.minDate.toDateString();
            },

            maxDate() {
                const r = /^[\-+]/.test(this.max)
                    ? $date.local().add(this.max).endOfDay()
                    : $date.local(this.max || null);
                if (!r.isValid()) {
                    console.error('ib-field-date: invalid format for max');
                }
                return r;
            },

            maxDateStr() {
                return this.maxDate.toDateString();
            },

            /** Convert stored value to displayed value */
            format(value) {
                return nativePicker
                    ? value => value == null ? '' : value
                    : value => {
                        const date = $date.local(value || null);
                        if (date.isValid()) {
                            this._pinit(this.value);
                            return this.formatter(date);
                        } else {
                            return '';
                        }
                    }
            },

            /** Convert displayed value to stored value */
            parse() {
                return nativePicker
                    ? value => value
                    : value => {
                        const all = value.match(this.parser);
                        if (all) {
                            return all[this.yy + 1] + '-' + pad(all[this.mm + 1]) + '-' + pad(all[this.dd + 1]);
                        }
                }
            }
        },

        watch: {
            min: 'input',
            max: 'input'
        },

        methods: {
            input() {
                this._sync();
                const input = this.$refs._in;
                if (!nativePicker) {
                    if (this.which != 8 && this.which != 46) {          // unless backspace or delete pressed last
                        input.value = input.value
                            .replace(/[^\d./-]/g, '')                   // remove unsupported characters
                            .replace(/\b(\d\W)/g, '0$1')                // add zero padding
                            .replace(this.match, this.replace)          // fields
                            .replace(/((?:\u200e|\u200f)?\W)+/g, '$1'); // remove repeats
                    }
                }
                this.empty = !input.value;
                this.validate();
                if (this.valid) {
                    this._pinit(this.toValue());
                }
                this._emit(this.toValue());
            },

            /** @private */
            _pinit(value) {
                if (this.picker && !nativePicker) {
                    return this.$overlay(picker, {
                        value,
                        min: this.min,
                        max: this.max,
                        anchor: this.$el,
                        align: 'start',
                        position: this.tooltip ? 'top' : 'bottom',
                        distant: this.distant
                    }, {
                        input: this._pset
                    });
                }
            },

            /** @private */
            _pset(value) {
                this._set(value);
                this.$emit('change', value);
            },

            /** @private */
            _pfocus() {
                const picker = this._pinit(this.value);
                if (picker) {
                    picker.open();
                }
            },

            /** @private */
            _pblur() {
                const picker = this._pinit(this.value);
                if (picker) {
                    picker.close();
                }
            },

            /** @private */
            keydown(e) {
                this._selectkey();
                this.which = e.which;
            },

            toValue() {
                const input = this.$refs._in;
                return input.value
                    ? this.valid
                        ? this.parse(input.value)
                        : undefined
                    : null;
            },

            /**
             * Set custom validity for min/max/pattern.
             */
            validate() {
                let validity = '';
                const input = this.$refs._in;
                if (!input.value) {
                    if (this.required) {
                        validity = (this.errorMissing || this.$locale.data.dateMissing);
                    }
                } else {
                    const value = this.parse(input.value);
                    if (fmtInvalid(value)) {
                        validity = (this.errorInvalid || this.$locale.data.dateInvalid);
                    } else if (value < this.minDateStr) {
                        validity = (this.errorUnderflow || this.$locale.data.dateMin).replace('{min}', this.formatter(this.minDate));
                    } else if (value > this.maxDateStr) {
                        validity = (this.errorOverflow || this.$locale.data.dateMax).replace('{max}', this.formatter(this.maxDate));
                    }
                }
                this.validity = validity;
                this.valid = !validity;
                this.__refresherror();
                return !validity;
            },

            getvalidity() {
                return this.validity;
            }
        }
    }
</script>

<docs>
    Examples:
    ```jsx
        new Vue({
            data() {
                return {
                    value: []
                }
            },
            template:
                `<ib-context>
                    <p class="after-sm">Basic Example: {{value[0]}}</p>
                    <p><ib-field-date v-model="value[0]"/></p>
                    <p class="before-lg after-sm">Today or Later: {{value[1]}}</p>
                    <p><ib-field-date v-model="value[1]" min="+P0D"/></p>
                    <p class="before-lg after-sm">Distant (e.g. Birthdate): {{value[2]}}</p>
                    <p><ib-field-date v-model="value[2]" distant/></p>
                </ib-context>`
        })
    ```
</docs>
